import ArrowForward from "@mui/icons-material/ArrowForward";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Card, IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { useId, useState } from "react";
import { Link as RouterLink } from "react-router";
import { usePendo } from "../../analytics/hooks/use-pendo-watch";
import { useProgramEngagement } from "../hooks/use-program-engagement";

import type { ProgramNode } from "../../program-tree/program-tree";
import type { FC, ReactNode } from "react";

/** "42%", the share of the people delivered to who clicked. */
export const engagementPercent = (clicked: number, delivered: number): string =>
  `${Math.round((clicked / delivered) * 100)}%`;

/** "142 of 376 people clicked · all time". */
export const engagementText = (clicked: number, delivered: number): string =>
  `${clicked.toLocaleString("en")} of ${delivered.toLocaleString("en")} ${
    delivered === 1 ? "person" : "people"
  } clicked · all time`;

const tip = (
  <Stack spacing={0.75} sx={{ p: 0.5, fontSize: 12, lineHeight: "18px" }}>
    <Box component="strong" sx={{ fontSize: 13, fontWeight: 600 }}>
      How engagement is calculated
    </Box>
    <span>
      The share of people who clicked a link, out of everyone these communications were delivered
      to. Each person is counted once, across all time.
    </span>
    <Box component="span" sx={{ color: "grey.300" }}>
      Only includes communications sent on a date, on a schedule or when published. Communications
      triggered by an event, and communications without links, aren’t counted.
    </Box>
  </Stack>
);

/** The large figure of the tile, with the words after it. */
const Figure: FC<{ value: string; muted?: boolean; children: ReactNode }> = ({
  value,
  muted = false,
  children,
}) => (
  <Stack direction="row" useFlexGap sx={{ alignItems: "baseline", flexWrap: "wrap", gap: 1 }}>
    <Typography variant="h5" component="p" sx={{ color: muted ? "text.disabled" : "primary.dark" }}>
      {value}
    </Typography>
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {children}
    </Typography>
  </Stack>
);

/**
 * The Engagement tile of a program's page, next to its counts: the share of the people that its
 * dated, repeating and published Communications with a link were delivered to who clicked a link,
 * with its folders, over all time, from program_engagement. Its info button explains the score on
 * hover, focus and click. An admin also gets a link to the Analytics dashboard, which only admins
 * can open.
 */
export const EngagementTile: FC<{ program: ProgramNode; admin: boolean }> = ({
  program,
  admin,
}) => {
  const { data, loading, error } = useProgramEngagement(program.id);
  const [open, setOpen] = useState(false);
  const pendo = usePendo();
  const labelId = useId();

  const show = (): void => {
    if (!open) pendo?.track("program-engagement-tooltip-opened", { programId: program.id });
    setOpen(true);
  };

  let figure: ReactNode;
  if (loading) figure = <Skeleton variant="text" width={160} sx={{ fontSize: 24 }} />;
  else if (error)
    figure = (
      <Figure value="—" muted>
        The engagement could not be loaded.
      </Figure>
    );
  else if (!data?.delivered)
    figure = (
      <Figure value="—" muted>
        No dated communications with links sent yet
      </Figure>
    );
  else
    figure = (
      <Figure value={engagementPercent(data.clicked, data.delivered)}>
        {engagementText(data.clicked, data.delivered)}
      </Figure>
    );

  return (
    <Card
      role="group"
      aria-labelledby={labelId}
      sx={{
        pl: 2,
        pr: 1,
        py: 1,
        borderRadius: "4px",
        boxShadow: "0 1px 6px rgba(100, 116, 139, 0.12)",
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.25} sx={{ alignItems: "center" }}>
          <Typography
            id={labelId}
            variant="overline"
            component="p"
            sx={{ color: "text.secondary" }}
          >
            Engagement
          </Typography>
          <Tooltip
            title={tip}
            describeChild
            open={open}
            onOpen={show}
            onClose={() => setOpen(false)}
            placement="bottom-end"
          >
            <IconButton
              size="small"
              aria-label="How engagement is calculated"
              onClick={show}
              sx={{ color: "text.secondary" }}
              data-analytics-id="program-engagement-info"
            >
              <InfoOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
        {figure}
      </Box>
      {admin ? (
        <IconButton
          component={RouterLink}
          to="/analytics"
          aria-label="Open Analytics"
          title="Open in Analytics"
          onClick={() =>
            pendo?.track("program-engagement-analytics-clicked", { programId: program.id })
          }
          sx={{ width: 44, height: 44, color: "primary.main" }}
          data-analytics-id="program-engagement-analytics"
        >
          <ArrowForward />
        </IconButton>
      ) : null}
    </Card>
  );
};
